package at.theatervogelweide.admin.contract

import at.theatervogelweide.domain.Bic
import at.theatervogelweide.domain.Iban
import at.theatervogelweide.domain.PhoneNumber

/**
 * Collects [FieldError]s; the web app runs the same rules before sending and the server runs them again.
 *
 * ```
 * validate {
 *     field("email", request.email) { required(); email() }
 * }
 * ```
 * Each field reports its first failing rule only.
 */
fun validate(block: Validation.() -> Unit): List<FieldError> = Validation().apply(block).errors

class Validation internal constructor() {
    internal val errors = mutableListOf<FieldError>()

    fun field(name: String, value: String, rules: FieldRules.() -> Unit) {
        val fieldRules = FieldRules(value).apply(rules)
        fieldRules.firstError?.let { errors += FieldError(name, it) }
    }

    /** A rule that isn't about one string, such as an end after a start; skipped if [name] has an error already. */
    fun check(name: String, valid: Boolean, message: () -> String) {
        if (!valid && errors.none { it.field == name }) {
            errors += FieldError(name, message())
        }
    }
}

class FieldRules internal constructor(private val value: String) {
    internal var firstError: String? = null
        private set

    private fun check(valid: Boolean, message: () -> String) {
        if (firstError == null && !valid) {
            firstError = message()
        }
    }

    fun required() = check(value.isNotBlank()) { "Bitte ausfüllen." }

    fun maxLength(length: Int) = check(value.length <= length) { "Höchstens $length Zeichen." }

    fun minLength(length: Int) = check(value.length >= length) { "Mindestens $length Zeichen." }

    /** A new password (#66): [PasswordRules]; the server also refuses common ones. */
    fun newPassword() {
        required()
        minLength(PasswordRules.MIN_LENGTH)
        // bcrypt ignores what comes after 72 bytes, so a longer password would only seem stronger
        check(value.encodeToByteArray().size <= PasswordRules.MAX_BYTES) {
            "Höchstens ${PasswordRules.MAX_BYTES} Zeichen; Umlaute und Sonderzeichen zählen doppelt."
        }
    }

    /** Only a plausibility check: one `@`, something before it and a dot in the domain. */
    fun email() = check(value.isBlank() || EMAIL.matches(value.trim())) { "Bitte eine gültige E-Mail-Adresse eingeben." }

    /** An IBAN, typed in any case and with spaces or not, whose check digits fit (#147); empty passes. */
    fun iban() = check(value.isBlank() || Iban.isValid(value)) { "Bitte eine gültige IBAN eingeben, z. B. AT61 1904 3002 3457 3201." }

    /** A phone number with digits to dial, e.g. `+43 699 12310780` (#147); empty passes. */
    fun phone() = check(value.isBlank() || PhoneNumber.isValid(value)) { "Bitte eine gültige Telefonnummer eingeben, z. B. +43 699 12310780." }

    /** A BIC of 8 or 11 characters (#147); empty passes. */
    fun bic() = check(value.isBlank() || Bic.isValid(value)) { "Bitte einen gültigen BIC eingeben (8 oder 11 Zeichen)." }

    private companion object {
        val EMAIL = Regex("^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$")
    }
}
